import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  BookOpen, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  Calendar,
  GitBranch,
  Lightbulb,
  Bug,
  Zap,
  Shield,
  Globe,
  Database,
  Code2
} from 'lucide-react';

/**
 * Development History - Documents key architectural decisions and implementation reasoning
 * This helps preserve institutional knowledge across development sessions
 */
const DevelopmentHistory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set(['march-2026']));

  const toggleItem = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Development history entries - organized by date/session
  const historyEntries = [
    {
      id: 'march-2026',
      date: 'March 2026',
      title: 'Multi-Club SaaS & Internationalization',
      entries: [
        {
          type: 'architecture',
          title: 'Hybrid Translation System',
          reasoning: 'We implemented a dual-approach translation system: react-i18next for static UI strings (fast, cached) and a server-side LLM API for dynamic content (forum posts, user-generated content). This balances performance with flexibility.',
          implementation: 'Static JSON files in /locales for 10 languages. TranslateButton component triggers API calls only when user clicks, with results cached in MongoDB to reduce costs.',
          files: ['frontend/src/i18n.js', 'backend/routers/translation.py', 'frontend/src/components/TranslateButton.jsx']
        },
        {
          type: 'feature',
          title: 'ELO Voting System with Custom Emojis',
          reasoning: 'Clubs wanted branded voting experiences. Rather than hardcoding emojis, we built an admin-configurable system with presets (paws, hearts, thumbs) plus custom emoji support.',
          implementation: 'ClubSettingsContext provides vote emojis app-wide. Admin Settings page has VoteEmojiSettings component. Backend stores vote_emoji_up, vote_emoji_down in club document.',
          files: ['frontend/src/contexts/ClubSettingsProvider.jsx', 'frontend/src/components/VoteEmojiSettings.jsx']
        },
        {
          type: 'architecture',
          title: 'Beta Gate with Crawler Bypass',
          reasoning: 'During beta, we need to gate access while still allowing Google to crawl for AdSense. Client-side user-agent detection lets search bots bypass the gate.',
          implementation: 'index.html has inline JS that checks navigator.userAgent against known crawler patterns before showing the gate.',
          files: ['frontend/public/index.html']
        },
        {
          type: 'bugfix',
          title: 'CAPTCHA System Disabled (Workaround)',
          reasoning: 'Cloudflare Turnstile was failing silently on mobile browsers (Android Chrome/Brave), blocking all registrations. Root cause unknown - could be widget rendering issue or callback firing problem.',
          implementation: 'Commented out TURNSTILE_SECRET_KEY in backend/.env. Registration works but is now unprotected. TODO: Debug mobile Turnstile or switch providers.',
          files: ['backend/.env', 'backend/routers/auth.py', 'frontend/src/pages/Auth.jsx']
        },
        {
          type: 'feature',
          title: 'Operations Roadmap System',
          reasoning: 'Needed to separate internal development tracking from user-facing beta feedback. Internal roadmap is admin-only, while beta feedback remains public.',
          implementation: 'New vbb_roadmap collection and API at /api/vbb/roadmap. OperationsRoadmap component in VBB Operations Center.',
          files: ['backend/routers/vbb_roadmap.py', 'frontend/src/components/OperationsRoadmap.jsx']
        },
        {
          type: 'security',
          title: 'GDPR Compliance & Privacy Controls',
          reasoning: 'EU users require explicit consent before any tracking. We implemented a tiered consent system with geo-detection.',
          implementation: 'PrivacyConsent component with 3 levels (essential/limited/full). Scripts only load after consent. Server stores consent records for audit.',
          files: ['frontend/src/components/PrivacyConsent.jsx', 'backend/routers/privacy.py']
        }
      ]
    },
    {
      id: 'feb-2026',
      date: 'February 2026',
      title: 'Regional NGO System & Adoption Features',
      entries: [
        {
          type: 'architecture',
          title: 'Parent/Chapter/Unitary NGO Model',
          reasoning: 'National NGOs like ASPCA have regional chapters. We needed a hierarchy that shows the relationship while letting chapters operate independently.',
          implementation: 'org_structure field (parent/chapter/unitary) in adoption_agencies. parent_org_id links chapters to parents. API filters by structure and region.',
          files: ['backend/routers/adoption_agencies.py', 'frontend/src/pages/AdoptionAgencies.jsx']
        },
        {
          type: 'feature',
          title: 'ELO Rating Algorithm',
          reasoning: 'Traditional star ratings are boring and gameable. ELO creates dynamic rankings where agencies compete based on user votes, making the leaderboard more engaging.',
          implementation: 'K-factor of 32 for sensitivity. Scores start at 1200. Backend calculates expected outcomes and adjusts both winner/loser ratings.',
          files: ['backend/services/elo.py', 'backend/routers/adoption_agencies.py']
        }
      ]
    },
    {
      id: 'jan-2026',
      date: 'January 2026',
      title: 'Core Platform Foundation',
      entries: [
        {
          type: 'architecture',
          title: 'Dual Admin System',
          reasoning: 'Platform operator (VBB) needs different permissions than club admins. Separated is_operator_admin from is_admin to prevent privilege escalation.',
          implementation: 'User model has is_admin (club-level) and is_operator_admin (platform-level). VBB Operations Center checks is_operator_admin.',
          files: ['backend/models/user.py', 'frontend/src/pages/VBBOperationsCenter.jsx']
        },
        {
          type: 'architecture',
          title: 'MongoDB ObjectId Handling',
          reasoning: 'ObjectIds are not JSON serializable. Every API response was failing with serialization errors until we standardized the pattern.',
          implementation: 'Always use {"_id": 0} in projections, or manually convert to string. Pydantic models exclude _id by default.',
          files: ['backend/routers/*.py']
        },
        {
          type: 'feature',
          title: 'Stripe Integration with EmergentIntegrations',
          reasoning: 'Using the emergentintegrations library provides a unified checkout interface that handles session management, webhooks, and status polling.',
          implementation: 'StripeCheckout class from emergentintegrations. Fixed pricing in MEMBERSHIP_PACKAGES dict (never from frontend). Webhook at /api/webhook/stripe.',
          files: ['backend/routers/payments.py']
        }
      ]
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'architecture': return <Database className="w-4 h-4 text-purple-400" />;
      case 'feature': return <Zap className="w-4 h-4 text-blue-400" />;
      case 'bugfix': return <Bug className="w-4 h-4 text-red-400" />;
      case 'security': return <Shield className="w-4 h-4 text-green-400" />;
      default: return <Code2 className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      architecture: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
      feature: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
      bugfix: 'bg-red-500/20 text-red-300 border-red-500/50',
      security: 'bg-green-500/20 text-green-300 border-green-500/50'
    };
    return colors[type] || 'bg-slate-500/20 text-slate-300 border-slate-500/50';
  };

  // Filter entries based on search
  const filteredEntries = historyEntries.map(section => ({
    ...section,
    entries: section.entries.filter(entry => 
      !searchQuery || 
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.reasoning.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.implementation.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.entries.length > 0);

  return (
    <div className="space-y-6" data-testid="development-history">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-amber-400" />
          <div>
            <h2 className="text-xl font-bold text-white">Development History</h2>
            <p className="text-sm text-slate-400">Architectural decisions & implementation reasoning</p>
          </div>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search decisions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-700 border-slate-600 text-white"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        <Badge variant="outline" className={getTypeBadge('architecture')}>
          <Database className="w-3 h-3 mr-1" /> Architecture
        </Badge>
        <Badge variant="outline" className={getTypeBadge('feature')}>
          <Zap className="w-3 h-3 mr-1" /> Feature
        </Badge>
        <Badge variant="outline" className={getTypeBadge('bugfix')}>
          <Bug className="w-3 h-3 mr-1" /> Bug Fix
        </Badge>
        <Badge variant="outline" className={getTypeBadge('security')}>
          <Shield className="w-3 h-3 mr-1" /> Security
        </Badge>
      </div>

      {/* Timeline */}
      {filteredEntries.map((section) => (
        <Card key={section.id} className="bg-slate-800/50 border-slate-700">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-700/50 transition-colors"
            onClick={() => toggleItem(section.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-amber-400" />
                <div>
                  <CardTitle className="text-white">{section.date}</CardTitle>
                  <CardDescription className="text-slate-400">{section.title}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-slate-400 border-slate-600">
                  {section.entries.length} items
                </Badge>
                {expandedItems.has(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </div>
            </div>
          </CardHeader>
          
          {expandedItems.has(section.id) && (
            <CardContent className="space-y-4 pt-0">
              {section.entries.map((entry, idx) => (
                <div 
                  key={idx} 
                  className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/50"
                >
                  <div className="flex items-start gap-3">
                    {getTypeIcon(entry.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-white">{entry.title}</h4>
                        <Badge variant="outline" className={`text-xs ${getTypeBadge(entry.type)}`}>
                          {entry.type}
                        </Badge>
                      </div>
                      
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-amber-400/80 font-medium mb-1 flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" /> Why?
                          </p>
                          <p className="text-slate-300">{entry.reasoning}</p>
                        </div>
                        
                        <div>
                          <p className="text-blue-400/80 font-medium mb-1 flex items-center gap-1">
                            <GitBranch className="w-3 h-3" /> How?
                          </p>
                          <p className="text-slate-300">{entry.implementation}</p>
                        </div>
                        
                        {entry.files && entry.files.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {entry.files.map((file, fidx) => (
                              <code 
                                key={fidx}
                                className="text-xs px-2 py-0.5 bg-slate-800 rounded text-slate-400"
                              >
                                {file}
                              </code>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      ))}

      {filteredEntries.length === 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No matching entries found</p>
          </CardContent>
        </Card>
      )}

      {/* Footer Note */}
      <div className="text-center text-xs text-slate-500 pt-4">
        This document is updated during each development session to preserve architectural knowledge.
      </div>
    </div>
  );
};

export default DevelopmentHistory;
